import { useEffect, useState } from 'react'
import { Video } from '~/components/ContentfulEntries/Video'
import { trackEvent } from '~/utils/tracking'
import { RichText } from '~/components/RichText'
import { createOptimizedContentfulImage, getContentfulAsset } from '~/libs/contentful/helpers'
import { FlexContainer, FlexItem } from '~/components/Grid'
import classNames from 'classnames'
import ProgressBarIcon from '~/components/animations/ProgressIcon'
import LoadingIcon from '~/components/animations/LoadingIcon'
import LinkIcon from '~/components/animations/LinkIcon'
import { TypeVideosBlock } from '~/types/contentful'
import { Document } from '@contentful/rich-text-types'

type FeatureVideosProps = {
  entry: TypeVideosBlock<'WITHOUT_UNRESOLVABLE_LINKS', string>
}

type TabNumber = 'first' | 'second' | 'third' | 'fourth'
const tabs = ['first', 'second', 'third', 'fourth'] as const

export const FeatureVideos = ({ entry }: FeatureVideosProps) => {
  const [activeTab, setActiveTab] = useState<TabNumber>('first')
  const [videoLoading, setVideoLoading] = useState<boolean>(false)
  const [tabTimeOutInSecond, setTabTimeOutInSecond] = useState(5)

  useEffect(() => {
    let timeId: NodeJS.Timeout

    if (!videoLoading) {
      timeId = setInterval(() => {
        const indexOfActiveItem = tabs.indexOf(activeTab)
        const nextItemIndex = tabs.length - 1 === indexOfActiveItem ? 0 : indexOfActiveItem + 1
        setActiveTab(tabs[nextItemIndex])
        setTabTimeOutInSecond(5)
      }, tabTimeOutInSecond * 1000)
    }
    return () => {
      clearTimeout(timeId)
    }
  }, [tabs, activeTab, tabTimeOutInSecond, videoLoading])

  if (!entry) {
    return null
  }

  const handleVideoOnLoadedDataEvent = (event: React.SyntheticEvent<HTMLMediaElement>) => {
    const videoElement = event.target as HTMLMediaElement

    setVideoLoading(false)
    setTabTimeOutInSecond(videoElement.duration)
  }

  const handleTabChangeEvent = (tab: TabNumber, label: string) => {
    setActiveTab(tab)
    trackEvent('homepage_toggle_resume_features', {
      block: entry.fields.blockId,
      homepage_resume_features: label,
    })
  }

  const activeTabEntry = getContentfulAsset(entry.fields[`${activeTab}SectionVideo`])

  return (
    <FlexContainer
      className="mt-24 justify-between max-md:mt-10"
      data-block-id={entry.fields.blockId}
    >
      <FlexItem desktop={12}>
        <div className="font-normal max-w-[700px] max-md:text-left max-md:mb-8 max-lg:text-center text-l mb-15">
          <RichText doc={entry.fields.titleWithCta as unknown as Document} />
        </div>
      </FlexItem>
      <FlexItem desktop={5} laptop={5} tablet={9}>
        {tabs.map((tab) => {
          const tabVideo = getContentfulAsset(entry.fields[`${activeTab}SectionVideo`])
          return (
            <div
              key={tab}
              className={classNames(
                'cursor-pointer py-7 pr-4 pl-5 max-md:py-6 max-lg:[&:not(:last-child)]:mb-2 rounded-3xl',
                {
                  'max-lg:bg-neutral-5 bg-transparent': activeTab === tab,
                  'bg-neutral-5 [&>h3]:mb-4': activeTab !== tab,
                },
              )}
              onClick={() =>
                handleTabChangeEvent(tab, entry.fields[`${tab}SectionTitle`] as string)
              }
            >
              <div className="flex justify-around">
                <h3
                  className="text-m max-md:m-0 inline-block font-normal text-neutral-100"
                  style={{ width: '90%' }}
                >
                  {entry.fields[`${tab}SectionTitle`]}
                </h3>
                <span style={{ width: '10%' }}>
                  {activeTab === tab ? (
                    videoLoading ? (
                      <LoadingIcon />
                    ) : (
                      <ProgressBarIcon tabTimeOutInSecond={tabTimeOutInSecond} />
                    )
                  ) : (
                    <LinkIcon />
                  )}
                </span>
              </div>

              {activeTab === tab && (
                <p className="text-xs text-neutral-50">{entry.fields[`${tab}SectionText`]}</p>
              )}

              {activeTab === tab && (
                <FlexItem desktop={7} laptop={7} tablet={5}>
                  <div className="block md:hidden overflow-hidden rounded-[20px]">
                    {tabVideo?.fields.file?.url.includes('images') ? (
                      <img
                        width={'100%'}
                        src={createOptimizedContentfulImage({ file: tabVideo.fields.file })}
                        alt={entry.fields[`${tab}SectionTitle`]}
                        loading="lazy"
                        className="flex"
                      />
                    ) : (
                      <Video
                        loop
                        autoPlay
                        muted
                        width={'100%'}
                        asset={tabVideo?.fields.file?.url as string}
                        onLoadStart={() => setVideoLoading(true)}
                        onLoadedData={() => setVideoLoading(false)}
                      />
                    )}
                  </div>
                </FlexItem>
              )}
            </div>
          )
        })}
      </FlexItem>
      <FlexItem
        desktop={6}
        laptop={6}
        tablet={5}
        className="hidden md:block overflow-hidden rounded-[20px]"
      >
        {activeTabEntry?.fields.file?.url.includes('images') ? (
          <img
            width={'100%'}
            src={createOptimizedContentfulImage({ file: activeTabEntry.fields.file })}
            alt={entry.fields[`${activeTab}SectionTitle`]}
            className="flex"
            loading="lazy"
          />
        ) : (
          <Video
            loop
            autoPlay
            muted
            width={'100%'}
            asset={activeTabEntry?.fields.file?.url}
            onLoadStart={() => setVideoLoading(true)}
            onLoadedData={handleVideoOnLoadedDataEvent}
          />
        )}
      </FlexItem>
    </FlexContainer>
  )
}
